<script setup lang="ts">
/**
 * Sparkline — inline SVG line chart with no dependencies. Renders up to
 * 60 points; older points scroll off the left as new samples arrive.
 *
 * Per challenger note: chart libraries (chart.js, d3) would balloon the
 * bundle. Hand-rolled SVG keeps the polyline at ~30 lines of script.
 */
import { computed } from 'vue';

const props = defineProps<{
  values: ReadonlyArray<number>;
  width?: number;
  height?: number;
  okMask?: ReadonlyArray<boolean>;
}>();

const width = computed(() => props.width ?? 320);
const height = computed(() => props.height ?? 60);

const path = computed(() => {
  const vals = props.values;
  if (vals.length === 0) return '';
  const max = Math.max(...vals, 1);
  const min = Math.min(...vals, 0);
  const range = Math.max(max - min, 1);
  const w = width.value;
  const h = height.value;
  const step = vals.length > 1 ? w / (vals.length - 1) : 0;
  return vals
    .map((v, i) => {
      const x = i * step;
      const y = h - ((v - min) / range) * h;
      return `${i === 0 ? 'M' : 'L'}${x.toFixed(2)},${y.toFixed(2)}`;
    })
    .join(' ');
});

const lastValue = computed(() => props.values[props.values.length - 1] ?? null);
const failedCount = computed(() => (props.okMask ? props.okMask.filter((b) => !b).length : 0));
</script>

<template>
  <figure class="cwc-sparkline">
    <svg
      :width="width"
      :height="height"
      :viewBox="`0 0 ${width} ${height}`"
      role="img"
      :aria-label="`Latency over the last ${values.length} samples`"
      data-testid="sparkline-svg"
    >
      <path
        v-if="path"
        :d="path"
        fill="none"
        stroke="currentColor"
        stroke-width="2"
        data-testid="sparkline-path"
      />
    </svg>
    <figcaption class="cwc-sparkline__caption">
      <span data-testid="sparkline-points">{{ values.length }} points</span>
      <span v-if="lastValue !== null" data-testid="sparkline-last">last: {{ lastValue }} ms</span>
      <span v-if="failedCount > 0" data-testid="sparkline-failed">{{ failedCount }} failed</span>
    </figcaption>
  </figure>
</template>

<style scoped>
.cwc-sparkline {
  margin: 0;
  display: flex;
  flex-direction: column;
  gap: var(--gl-spacing-2);
  color: var(--gl-accent-primary);
}

.cwc-sparkline__caption {
  display: flex;
  gap: var(--gl-spacing-3);
  color: var(--gl-fg-subtle);
  font-size: var(--gl-font-size-sm);
}
</style>
